/**
 * Pretty labels and ordering for raw stat fields in the hover card.
 *
 * The optimizer cube can carry any subset of these — we promote the
 * familiar ones (impressions, clicks, conversions, cost, revenue, profit,
 * ROI) to the top and render anything else after.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

// Labels match the tag-rule field dropdown (LIST_TAG_RULE_FIELD_LABELS) so a
// rule reading "Reg2Dep ≤ 3" can be checked against the same row in this card.
const HIGHLIGHT_ORDER: ReadonlyArray<{ id: string; label: string }> = [
  { id: 'impressions', label: 'Impressions' },
  { id: 'visits', label: 'Visits' },
  { id: 'uniqueVisits', label: 'Unique visits' },
  { id: 'clicks', label: 'Clicks' },
  { id: 'uniqueClicks', label: 'Unique clicks' },
  { id: 'conversions', label: 'Conversions' },
  { id: 'registrations', label: 'Registrations' },
  { id: 'ftd', label: 'FTD' },
  { id: 'revenue', label: 'Revenue' },
  { id: 'cost', label: 'Cost' },
  { id: 'profit', label: 'Profit' },
  { id: 'roi', label: 'ROI' },
  { id: 'ctr', label: 'CTR' },
  { id: 'cr', label: 'CR' },
  { id: 'cv', label: 'CV' },
  { id: 'ImCTR', label: 'SMS CTR' },
  { id: 'uCTR', label: 'uCTR' },
  { id: 'Click2Reg', label: 'Click2Reg' },
  { id: 'Reg2Dep', label: 'Reg2Dep' },
  { id: 'epc', label: 'EPC' },
  { id: 'epl', label: 'EPL' },
  { id: 'uEPC', label: 'uEPC' },
  { id: 'epv', label: 'EPV' },
  { id: 'uEPV', label: 'uEPV' },
  { id: 'CPuC', label: 'CPuC' },
  { id: 'CPL', label: 'CPL' },
  { id: 'CPS', label: 'CPS' },
];

const LABEL_BY_ID = new Map(HIGHLIGHT_ORDER.map((m) => [m.id, m.label] as const));

export function metricLabel(id: string): string {
  return LABEL_BY_ID.get(id) ?? prettify(id);
}

export interface OrderedMetric {
  id: string;
  label: string;
  value: number;
}

export function orderMetrics(metrics: Record<string, number>): OrderedMetric[] {
  const out: OrderedMetric[] = [];
  const seen = new Set<string>();
  for (const m of HIGHLIGHT_ORDER) {
    if (!(m.id in metrics)) continue;
    out.push({ id: m.id, label: m.label, value: metrics[m.id] });
    seen.add(m.id);
  }
  for (const [id, value] of Object.entries(metrics)) {
    if (seen.has(id)) continue;
    out.push({ id, label: prettify(id), value });
  }
  return out;
}

function prettify(id: string): string {
  return id
    .replace(/[_-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^\w/, (c) => c.toUpperCase());
}
